import { type ReactElement } from 'react';

import { StatusStep } from '../ApplicationStatusTracker';
import {
  deriveSequentialStepState,
  getPhasePosition,
  PHASE,
  type PhasePosition,
  STATE_DISPLAY,
  type StepStateName,
} from './applicationStatusPhases';
import { mapRegionStatus, type SubStatusRegion, type SubStatusRegions } from './regionStatusMapping';
import { STEP_CONTENT, type TrackerStepKey } from './stepContent';

export type TrackerOutcome = 'ACTIVE' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'DENIED';
export type TrackerPhase =
  | 'ONBOARDING'
  | 'SUBMIT_APPLICATION'
  | 'SPONSOR_REVIEW'
  | 'FOREIGN_NATIONAL_REVIEW'
  | 'SECURITY_REVIEW'
  | 'PRESCREENING';

export interface TrackerState {
  outcome: TrackerOutcome;
  phase: TrackerPhase | null;
  statusAt?: string | null;
}

/**
 * Build the full 8-step application status tracker (ACP-1040) from the real
 * application state.
 *
 * - Sequential steps (1-5, 8) derive their state from `applicationStatus` via the
 *   phase model in applicationStatusPhases.
 * - The parallel fingerprint + eApp steps (6, 7) derive their state from the
 *   per-region `applicationSubStatus` object once the application reaches the
 *   vetting phase; before/after that they follow the overall phase.
 *
 * Copy is static per step (see stepContent); the state only drives badge + icon.
 * With no application data (loading / no id / error) every step renders neutral.
 */

type StepDef =
  | { id: string; key: TrackerStepKey; kind: 'sequential'; phase: number }
  | { id: string; key: TrackerStepKey; kind: 'region'; region: SubStatusRegion };

// Display order. Ids are kept stable (step-1..step-5, step-fingerprints, step-eapp,
// step-prescreening-determination) so existing test ids / deep links keep working.
const STEP_ORDER: StepDef[] = [
  { id: 'step-1', key: 'onboarding', kind: 'sequential', phase: PHASE.onboarding },
  { id: 'step-2', key: 'submit', kind: 'sequential', phase: PHASE.submit },
  { id: 'step-3', key: 'sponsor', kind: 'sequential', phase: PHASE.sponsor },
  { id: 'step-4', key: 'foreignNational', kind: 'sequential', phase: PHASE.foreignNational },
  { id: 'step-5', key: 'security', kind: 'sequential', phase: PHASE.security },
  { id: 'step-fingerprints', key: 'fingerprints', kind: 'region', region: 'fingerprints' },
  { id: 'step-eapp', key: 'eapp', kind: 'region', region: 'eapp' },
  { id: 'step-prescreening-determination', key: 'prescreening', kind: 'sequential', phase: PHASE.prescreening },
];

const TRACKER_PHASE_POSITION: Record<TrackerPhase, number> = {
  FOREIGN_NATIONAL_REVIEW: PHASE.foreignNational,
  ONBOARDING: PHASE.onboarding,
  PRESCREENING: PHASE.prescreening,
  SECURITY_REVIEW: PHASE.security,
  SPONSOR_REVIEW: PHASE.sponsor,
  SUBMIT_APPLICATION: PHASE.submit,
};

function getTerminalBadge(outcome: TrackerOutcome) {
  switch (outcome) {
    case 'REJECTED':
      return 'rejected' as const;
    case 'CANCELLED':
      return 'cancelled' as const;
    case 'DENIED':
      return 'denied' as const;
    default:
      return undefined;
  }
}

/** State name for a parallel region step (fingerprints / eApp). */
function deriveRegionStepState(
  region: SubStatusRegion,
  pos: PhasePosition | undefined,
  subStatus: SubStatusRegions | undefined,
): StepStateName {
  if (!pos) return 'pending';
  if (pos.allComplete) return 'complete';
  if (PHASE.vetting < pos.phase) return 'complete'; // application is past vetting
  if (PHASE.vetting > pos.phase) return 'pending'; // application hasn't reached vetting yet

  // At the vetting phase: reflect the region's own sub-status. Reuse the region
  // status→display mapping (we only need its step status + badge here).
  const display = mapRegionStatus(region, subStatus?.[region]);
  switch (display.stepStatus) {
    case 'completed':
      return 'complete';
    case 'current':
      return 'current';
    case 'error':
      return 'attention';
    default:
      return 'pending';
  }
}

function renderBody(key: TrackerStepKey): ReactElement {
  const { paragraphs } = STEP_CONTENT[key];

  return (
    <div>
      {paragraphs.map((text, i) => (
        <p
          key={i}
          className={i === 0 ? 'ds-u-margin-top--0' : ''}>
          {text}
        </p>
      ))}
    </div>
  );
}

export function buildTrackerSteps(
  applicationStatus?: string,
  subStatus?: SubStatusRegions,
  trackerState?: TrackerState,
): StatusStep[] {
  const pos = getPhasePosition(applicationStatus);
  const terminalBadge = trackerState ? getTerminalBadge(trackerState.outcome) : undefined;
  const terminalPhase = trackerState?.phase ? TRACKER_PHASE_POSITION[trackerState.phase] : undefined;

  return STEP_ORDER.map((def) => {
    const stepPhase = def.kind === 'region' ? PHASE.vetting : def.phase;

    if (terminalBadge && terminalPhase !== undefined) {
      if (stepPhase < terminalPhase) {
        return {
          badgeVariant: 'complete',
          content: renderBody(def.key),
          id: def.id,
          status: 'completed',
          title: STEP_CONTENT[def.key].title,
        };
      }

      if (stepPhase === terminalPhase) {
        return {
          badgeVariant: terminalBadge,
          content: renderBody(def.key),
          id: def.id,
          status: 'terminal',
          title: STEP_CONTENT[def.key].title,
        };
      }

      return {
        badgeVariant: undefined,
        content: renderBody(def.key),
        id: def.id,
        status: 'pending',
        title: STEP_CONTENT[def.key].title,
      };
    }

    if (def.kind === 'region') {
      const stateName = deriveRegionStepState(def.region, pos, subStatus);
      const { stepStatus, badgeVariant } = STATE_DISPLAY[stateName];

      return {
        badgeVariant,
        content: renderBody(def.key),
        id: def.id,
        status: stepStatus,
        title: STEP_CONTENT[def.key].title,
      };
    }

    const stateName = deriveSequentialStepState(def.phase, pos);
    const { stepStatus, badgeVariant } = STATE_DISPLAY[stateName];

    return {
      badgeVariant,
      content: renderBody(def.key),
      id: def.id,
      status: stepStatus,
      title: STEP_CONTENT[def.key].title,
    };
  });
}
